'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { trpc, trpcVanilla } from '@/lib/trpc';

import { useRegistrationStore } from '../../stores/registration-store';
import { trackRego } from '../../lib/registration-analytics';
import {
  buildCleanedReturnUrl,
  resolveSettlementOutcome,
  SETTLEMENT_POLL_INTERVAL_MS,
  SETTLEMENT_TIMEOUT_MS,
  type HostedCheckoutReturn,
} from '../lib/hostedCheckoutReturn';
import type { CheckoutReturnFailure, RegistrationCompletionResult } from '../types';

interface UseCheckoutReturnInput {
  /** Return params captured once at mount (parseHostedCheckoutReturn) */
  checkoutReturn: HostedCheckoutReturn;
  /** Event UUID from the return URL; getStatus.eventId is the fallback */
  eventId: string | null;
  entrantEmail: string | null;
  newsletterOptIn: boolean;
  marketingOptIns: Record<string, boolean>;
  onCompleted: (result: RegistrationCompletionResult) => void;
  /** Payment is (probably) fine but we can't confirm client-side — webhook finishes it */
  onAwaitingPayment: (donationId: string) => void;
  onReturnFailure: (failure: CheckoutReturnFailure) => void;
}

/**
 * Checkout return state machine: cleans the return params off the URL,
 * polls publicDonation.getStatus after a success return until the webhook
 * settles the donation, then completes the registration (idempotent — the
 * webhook may have already done it server-side).
 *
 * Cancel returns don't poll: the donor backed out of the checkout page, so we
 * surface a retry banner over the restored cart.
 */
export function useCheckoutReturn(input: UseCheckoutReturnInput) {
  const {
    checkoutReturn,
    eventId,
    entrantEmail,
    newsletterOptIn,
    marketingOptIns,
    onCompleted,
    onAwaitingPayment,
    onReturnFailure,
  } = input;

  const donationId = checkoutReturn.kind === 'success' ? checkoutReturn.donationId : null;
  const [isSettling, setIsSettling] = useState(checkoutReturn.kind === 'success');

  // Latest-value refs so the resolution effect doesn't re-fire on every render
  const latest = useRef({ entrantEmail, newsletterOptIn, marketingOptIns });
  latest.current = { entrantEmail, newsletterOptIn, marketingOptIns };
  const callbacks = useRef({ onCompleted, onAwaitingPayment, onReturnFailure });
  callbacks.current = { onCompleted, onAwaitingPayment, onReturnFailure };

  const resolvedRef = useRef(false);
  const startRef = useRef(0);
  const pollCountRef = useRef(0);

  // One-shot mount handling: clean URL, dispatch cancel returns, start the clock
  useEffect(() => {
    if (checkoutReturn.kind === 'none') return;

    window.history.replaceState(
      window.history.state,
      '',
      buildCleanedReturnUrl(window.location.href),
    );

    if (checkoutReturn.kind === 'cancelled') {
      trackRego('rego_settlement_resolved', { outcome: 'user-cancelled' });
      callbacks.current.onReturnFailure({ donationId: null, reason: 'user-cancelled' });
      return;
    }

    startRef.current = Date.now();
    trackRego('rego_awaiting_settlement', { donation_id: checkoutReturn.donationId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusQuery = trpc.publicDonation.getStatus.useQuery(
    { donationId: donationId ?? '' },
    {
      enabled: isSettling && Boolean(donationId),
      refetchInterval: SETTLEMENT_POLL_INTERVAL_MS,
      retry: false,
    },
  );

  const completeAfterPayment = useCallback(
    async (settledDonationId: string, settledEventId: string | null) => {
      const completionEventId = eventId ?? settledEventId;
      const pollDurationMs = Date.now() - startRef.current;
      try {
        if (!completionEventId) {
          throw new Error('Missing eventId for registration completion');
        }
        await trpcVanilla.publicEntrant.completeRegistration.mutate({
          eventId: completionEventId,
          registrationDonationId: settledDonationId,
          termsAccepted: true,
          newsletterOptIn: latest.current.newsletterOptIn,
          marketingOptIns: latest.current.marketingOptIns,
        });
        trackRego('rego_settlement_resolved', {
          donation_id: settledDonationId,
          outcome: 'completed',
          poll_duration_ms: pollDurationMs,
          poll_count: pollCountRef.current,
        });
        trackRego('rego_completed', {
          donation_id: settledDonationId,
          is_waitlist: false,
          is_redirect_return: true,
        });
        useRegistrationStore.getState().reset();
        callbacks.current.onCompleted({
          email: latest.current.entrantEmail ?? '',
          donationId: settledDonationId,
          isWaitlist: false,
        });
      } catch {
        // Payment settled but completion failed (e.g. session lost across the
        // redirect) — the webhook completes registration server-side.
        trackRego('rego_settlement_resolved', {
          donation_id: settledDonationId,
          outcome: 'payment_received_unauthenticated',
          poll_duration_ms: pollDurationMs,
          poll_count: pollCountRef.current,
        });
        callbacks.current.onAwaitingPayment(settledDonationId);
      }
    },
    [eventId],
  );

  // Resolve on every poll result (or error) — plus a hard deadline below
  useEffect(() => {
    if (!isSettling || !donationId || resolvedRef.current) return;
    if (statusQuery.dataUpdatedAt === 0 && statusQuery.errorUpdatedAt === 0) return;
    pollCountRef.current += 1;

    const outcome = resolveSettlementOutcome({
      status: statusQuery.data?.status,
      elapsedMs: Date.now() - startRef.current,
    });
    if (outcome === 'polling') return;

    resolvedRef.current = true;
    setIsSettling(false);

    if (outcome === 'paid') {
      void completeAfterPayment(donationId, statusQuery.data?.eventId ?? null);
      return;
    }
    if (outcome === 'timeout') {
      trackRego('rego_settlement_timeout', {
        donation_id: donationId,
        poll_duration_ms: Date.now() - startRef.current,
        poll_count: pollCountRef.current,
      });
      callbacks.current.onAwaitingPayment(donationId);
      return;
    }
    trackRego('rego_settlement_resolved', {
      donation_id: donationId,
      outcome,
      poll_duration_ms: Date.now() - startRef.current,
      poll_count: pollCountRef.current,
      settled_status: statusQuery.data?.status,
    });
    callbacks.current.onReturnFailure({ donationId, reason: outcome });
  }, [
    isSettling,
    donationId,
    statusQuery.dataUpdatedAt,
    statusQuery.errorUpdatedAt,
    statusQuery.data?.status,
    statusQuery.data?.eventId,
    completeAfterPayment,
  ]);

  // Hard deadline backstop — fires even if every poll request errors out
  useEffect(() => {
    if (!isSettling || !donationId) return;
    const timer = setTimeout(() => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      setIsSettling(false);
      trackRego('rego_settlement_timeout', {
        donation_id: donationId,
        poll_duration_ms: Date.now() - startRef.current,
        poll_count: pollCountRef.current,
      });
      callbacks.current.onAwaitingPayment(donationId);
    }, SETTLEMENT_TIMEOUT_MS + SETTLEMENT_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [isSettling, donationId]);

  return { isSettling };
}
